import { isValidGs1 } from './gs1CheckDigit';

/**
 * Pure input validators. Each returns the helper text to show, or null when the
 * value is valid. An empty value is valid everywhere: required-ness is the call
 * site's decision.
 */

const isEmpty = (value: string | null | undefined): boolean => !String(value ?? '').trim();
const clean = (value: string | null | undefined): string => String(value ?? '').trim();
const countryOf = (country?: string): string => String(country || 'US').trim().toUpperCase();

export function validateEmail(value: string): string | null {
  if (isEmpty(value)) return null;
  const v = clean(value);
  if (/\s/.test(v) || !/^[^@\s]+@[^@\s]+\.[^@\s.]{2,}$/.test(v) || v.includes('..')) {
    return 'Enter a valid email address, like name@example.com';
  }
  return null;
}

/** Adds https:// to a scheme-less value; leaves http(s) values alone. */
export function normalizeUrl(value: string): string {
  const v = clean(value);
  if (!v) return '';
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return v;
  return `https://${v.replace(/^\/+/, '')}`;
}

export function validateUrl(value: string, options: { allowSchemeless?: boolean } = {}): string | null {
  if (isEmpty(value)) return null;
  const v = clean(value);
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(v);
  if (!hasScheme && !options.allowSchemeless) return 'Start the address with http:// or https://';
  if (/\s/.test(v)) return 'Enter a valid web address, like https://example.com';
  try {
    const url = new URL(hasScheme ? v : normalizeUrl(v));
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'Use an http:// or https:// address';
    if (!url.hostname.includes('.') && url.hostname !== 'localhost') {
      return 'Enter a valid web address, like https://example.com';
    }
    return null;
  } catch {
    return 'Enter a valid web address, like https://example.com';
  }
}

export function validatePhone(value: string): string | null {
  if (isEmpty(value)) return null;
  const v = clean(value);
  if (v.startsWith('+')) {
    const digits = v.slice(1).replace(/[\s().-]/g, '');
    if (!/^\d{7,15}$/.test(digits)) return 'Enter an international number like +44 20 7946 0958';
    return null;
  }
  if (!/^[\d\s().-]+$/.test(v)) return 'Enter a 10-digit phone number, like (555) 123-4567';
  let digits = v.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return 'Enter a 10-digit phone number, like (555) 123-4567';
  if (digits[0] === '0' || digits[0] === '1') return 'Area code cannot start with 0 or 1';
  return null;
}

/** NANP numbers become (555) 123-4567; international numbers are left as typed, trimmed. */
export function normalizePhone(value: string): string {
  const v = clean(value);
  if (!v) return '';
  if (v.startsWith('+')) return v.replace(/\s+/g, ' ');
  let digits = v.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return v;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function validatePostalCode(value: string, country?: string): string | null {
  if (isEmpty(value)) return null;
  const v = clean(value);
  const c = countryOf(country);
  if (c === 'US') {
    return /^\d{5}(-\d{4})?$/.test(v) ? null : 'Enter a ZIP code like 94103 or 94103-1234';
  }
  if (c === 'CA') {
    return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(v)
      ? null
      : 'Enter a postal code like K1A 0B1';
  }
  return v.length <= 12 ? null : 'Enter a valid postal code';
}

export function normalizePostalCode(value: string, country?: string): string {
  const v = clean(value);
  if (!v) return '';
  const c = countryOf(country);
  if (c === 'CA') {
    const compact = v.replace(/[\s-]/g, '').toUpperCase();
    return compact.length === 6 ? `${compact.slice(0, 3)} ${compact.slice(3)}` : v.toUpperCase();
  }
  if (c === 'US') {
    const digits = v.replace(/\s/g, '');
    if (/^\d{9}$/.test(digits)) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  }
  return v;
}

/**
 * Mirrors the API's storeOnboardingSchema (src/validators/storeOnboarding.ts):
 * US = EIN `NN-NNNNNNN`; Canada = exactly 9 digits once spaces and dashes are
 * stripped (the RT program id is not part of a business number). Validation is
 * run on the normalized value, so callers must submit `normalizeTaxId(...)`.
 */
export function validateTaxId(value: string, country?: string): string | null {
  if (isEmpty(value)) return null;
  const v = normalizeTaxId(value, country);
  const c = countryOf(country);
  if (c === 'US') {
    return /^\d{2}-\d{7}$/.test(v) ? null : 'Enter an EIN like 12-3456789';
  }
  if (c === 'CA') {
    return /^\d{9}$/.test(v) ? null : 'Enter a 9-digit business number, without the RT0001 program id';
  }
  return null;
}

export function normalizeTaxId(value: string, country?: string): string {
  const v = clean(value);
  if (!v) return '';
  const c = countryOf(country);
  if (c === 'US') {
    const compact = v.replace(/\s/g, '');
    if (/^\d{9}$/.test(compact)) return `${compact.slice(0, 2)}-${compact.slice(2)}`;
    return compact;
  }
  if (c === 'CA') {
    const stripped = v.replace(/[\s-]/g, '');
    return /^\d{9}$/.test(stripped) ? stripped : v;
  }
  return v;
}

export function validateUpc(value: string): string | null {
  if (isEmpty(value)) return null;
  const v = clean(value);
  if (!/^\d+$/.test(v)) return 'Use digits only';
  if (![8, 12, 13, 14].includes(v.length)) return 'A barcode has 8, 12, 13 or 14 digits';
  return isValidGs1(v) ? null : 'The check digit does not match, so this barcode has a typo';
}

export function validateYearMonth(value: string): string | null {
  if (isEmpty(value)) return null;
  const m = clean(value).match(/^(\d{4})-(\d{2})$/);
  if (!m) return 'Use YYYY-MM, like 2025-10';
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? null : 'Month must be 01 to 12';
}

const CRON_FIELDS: Array<{ name: string; min: number; max: number }> = [
  { name: 'minute', min: 0, max: 59 },
  { name: 'hour', min: 0, max: 23 },
  { name: 'day of month', min: 1, max: 31 },
  { name: 'month', min: 1, max: 12 },
  { name: 'day of week', min: 0, max: 7 },
];

const cronPartValid = (part: string, min: number, max: number): boolean => {
  const [range, stepRaw, ...extra] = part.split('/');
  if (extra.length) return false;
  if (stepRaw !== undefined && (!/^\d+$/.test(stepRaw) || Number(stepRaw) < 1)) return false;
  if (range === '*') return true;
  const bounds = range.split('-');
  if (bounds.length > 2 || !bounds.every((b) => /^\d+$/.test(b))) return false;
  const nums = bounds.map(Number);
  if (nums.some((n) => n < min || n > max)) return false;
  return nums.length === 1 || nums[0] <= nums[1];
};

/** Five-field cron (minute hour day-of-month month day-of-week). Numbers, ranges, lists and steps only. */
export function validateCron(value: string): string | null {
  if (isEmpty(value)) return null;
  const fields = clean(value).split(/\s+/);
  if (fields.length !== 5) return 'A schedule has 5 fields: minute hour day-of-month month day-of-week';
  for (let i = 0; i < 5; i++) {
    const { name, min, max } = CRON_FIELDS[i];
    if (!fields[i].split(',').every((p) => cronPartValid(p, min, max))) {
      return `Invalid ${name} field "${fields[i]}" (allowed ${min}-${max})`;
    }
  }
  return null;
}
